import React, { useCallback, useRef, useState } from 'react';

import { mapCredibilityFlag } from '@repo/analysis-core';
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/ui/components/ui/tooltip';

import { AnalysisTooltipContent } from '@/components/article-analysis/analysis-container';

interface DisclaimerSectorProps {
  disclaimer: string;
  credibilityFlag?: string | null;
}

export const DisclaimerSector: React.FC<DisclaimerSectorProps> = ({
  disclaimer,
  credibilityFlag,
}) => {
  // CRE-987: never render a raw verification-framing flag (e.g. "Verified
  // Trial Data") as visible copy on claimed/company-reported data.
  const flagLabel = mapCredibilityFlag(credibilityFlag) || 'Consider';
  const [isDisclaimerTooltipOpen, setIsDisclaimerTooltipOpen] = useState(false);

  const disclaimerTooltipRef = useRef<HTMLParagraphElement>(null);

  const handleDisclaimerTooltipOpenChange = useCallback(
    (open: boolean) => {
      if (disclaimerTooltipRef.current) {
        const isClamped =
          disclaimerTooltipRef.current.scrollHeight >
          disclaimerTooltipRef.current.clientHeight;
        setIsDisclaimerTooltipOpen(open && isClamped);
      }
    },
    [setIsDisclaimerTooltipOpen],
  );

  return (
    <TooltipProvider>
      <Tooltip
        delayDuration={200}
        open={isDisclaimerTooltipOpen}
        onOpenChange={handleDisclaimerTooltipOpenChange}
      >
        <TooltipTrigger asChild>
          <div className="w-full bg-[#1E3A8A] px-2 py-1.5 @cred-md:px-[9.5px] @cred-md:py-[7px] @cred-lg:px-[10.13px] @cred-lg:py-[7.5px] @cred-xl:px-[13.33px] @cred-xl:py-2.5">
            <p
              ref={disclaimerTooltipRef}
              className="line-clamp-5 whitespace-break-spaces text-left text-[10px] font-medium leading-[13px] text-white @cred-md:text-xs @cred-md:leading-[15.2px] @cred-lg:text-[13px] @cred-lg:leading-[16.5px] @cred-xl:text-base @cred-xl:leading-[21.7px]"
            >
              <b>{flagLabel}:</b> {disclaimer}
            </p>
          </div>
        </TooltipTrigger>
        <AnalysisTooltipContent className="z-200 max-w-72 bg-white shadow-[0_4px_12px_rgba(0,0,0,0.3)] @cred-md:max-w-80 @cred-lg:max-w-[330px] @cred-xl:max-w-[440px]">
          <p className="text-[10px] font-normal leading-[13px] text-neutral-2 @cred-md:text-xs @cred-md:leading-[15.2px] @cred-lg:text-[13px] @cred-lg:leading-[16.5px] @cred-xl:text-base @cred-xl:leading-[21.7px]">
            <b>{flagLabel}:</b> {disclaimer}
          </p>
        </AnalysisTooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
